/**
 * CONTAS E CONCILIAÇÃO: AS REGRAS DA ESCRITA (10/10/2026, medição do item 13). Funções puras que a BankAccountsTab
 * usa e os testes cobram dos dois lados. Os confirms seguem a forma do useConfirmarPerigo: o 1º parágrafo é o título
 * (a casca o trunca numa linha), o resto é o corpo.
 */

const reais = (v: any) => {
  const n = parseFloat(String(v));
  return Number.isFinite(n) ? `R$ ${Math.abs(n).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'R$ —';
};
const dataBr = (d: any) => {
  const s = String(d || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s.split('-').reverse().join('/') : '—';
};

/** A transação está ligada a fatura, despesa ou empréstimo (o que a classificação passaria por cima)? */
export function temVinculo(tx: any): boolean {
  return !!(tx?.invoiceId || tx?.expenseId || tx?.loanContractId || tx?.expense
    || (tx?.expensePayments?.length || 0) > 0 || (tx?.invoicePayments?.length || 0) > 0);
}

/**
 * D2 (front): classificar só em linha PENDENTE, ou numa linha cuja única "conciliação" é a própria classificação
 * (para trocar ou tirar a categoria). O botão aparecia em todas as linhas, e o servidor grava a categoria por cima do
 * vínculo com fatura ou despesa, sem guarda (caso real em produção: tx 1651, 16/09). A recusa no servidor é do Bastião.
 */
export function podeClassificar(tx: any): boolean {
  return !tx?.isReconciled || !temVinculo(tx);
}

/**
 * D3: excluir transação diz qual e o que sai junto. No servidor: os pagamentos ligados a ela são apagados; a despesa
 * criada PELA conciliação é apagada, e a que já existia volta a ficar em aberto por esse valor; o pagamento da fatura
 * é apagado SEM recalcular o status da fatura (se estava Paga, continua Paga).
 */
export function confirmacaoDeExcluirTransacao(tx: any): string {
  const sinal = tx?.type === 'credit' ? '+' : '−';
  const linhas: string[] = [
    'Excluir esta transação?',
    `${dataBr(tx?.date)} · ${sinal}${reais(tx?.amount)} · "${tx?.description || 'sem descrição'}".`,
  ];
  const fatura = tx?.invoicePayments?.[0]?.invoice || tx?.invoice;
  if (fatura || (tx?.invoicePayments?.length || 0) > 0 || tx?.invoiceId) {
    const codigo = fatura?.code ? `da fatura ${fatura.code}` : 'da fatura';
    linhas.push(`O pagamento ${codigo} ligado a ela é apagado, mas o status da fatura NÃO é recalculado: se ela estava Paga, continua Paga, sem pagamento.`);
  }
  const despesa = tx?.expensePayments?.[0]?.expense || tx?.expense;
  if (despesa || tx?.expenseId || (tx?.expensePayments?.length || 0) > 0) {
    const nome = despesa?.description ? `"${despesa.description}"` : 'ligada a ela';
    linhas.push(despesa?.createdByReconciliation === true
      ? `A despesa ${nome} foi criada pela conciliação e é APAGADA junto.`
      : despesa?.createdByReconciliation === false
        ? `O pagamento da despesa ${nome} é apagado, e ela volta a ficar em aberto por esse valor.`
        : `O pagamento da despesa ${nome} é apagado. Se ela foi criada pela conciliação, é apagada junto; se já existia, volta a ficar em aberto.`);
  }
  if (tx?.loanContractId) linhas.push('O vínculo com a operação de crédito sai junto.');
  linhas.push('O saldo da conta é recalculado. Esta ação não pode ser desfeita.');
  return linhas.join('\n\n');
}

/** D7: o extrato oficial é um por mês e por conta; enviar outro SUBSTITUI. */
export function avisoDeSubstituicaoDoExtrato(existente: any | undefined, rotuloDoMes: string): string | null {
  if (!existente) return null;
  const quando = existente.createdAt || existente.uploadedAt;
  return `Já existe extrato oficial de ${rotuloDoMes}: ${existente.originalName || 'arquivo sem nome'}${quando ? `, enviado em ${dataBr(quando)}` : ''}. Enviar este SUBSTITUI aquele arquivo.`;
}

export function confirmacaoDeExcluirExtrato(stmt: any, rotuloDoMes: string): string {
  return [
    `Excluir o extrato oficial de ${rotuloDoMes}?`,
    `${stmt?.originalName || 'arquivo sem nome'}. É o único extrato oficial desta conta neste mês, e o PDF é apagado do armazenamento.`,
  ].join('\n\n');
}
